import { User } from 'lucide-react';
import type { ReactNode } from 'react';

import { initials } from '../../../chrome/shell/initials';
import { Avatar, AvatarFallback, AvatarGroup, AvatarImage } from '../../../ui/avatar';
import { media } from './messages';

/** An account's own stored profile picture, falling back to its initials (or a
 *  generic silhouette when there is no name to initial either) — `AvatarImage`
 *  is Radix's own, so it already withholds itself on a failed or slow load
 *  until the fallback is the only thing that needs rendering. */
export function AccountAvatar(props: {
  avatarUrl: string | null;
  name: string | null;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}): ReactNode {
  return (
    <Avatar size={props.size} className={props.className} aria-hidden>
      {props.avatarUrl !== null && <AvatarImage src={props.avatarUrl} alt="" />}
      <AvatarFallback>
        {props.name === null ? <User aria-hidden className="size-4" /> : initials(props.name)}
      </AvatarFallback>
    </Avatar>
  );
}

export interface Coauthor {
  account_id: string;
  handle: string | null;
  avatar_url: string | null;
}

/** A collab post's other authors, past the main byline: a small overlapping
 *  stack of avatars, named for a screen reader as one label rather than one
 *  per avatar (each is `aria-hidden` on its own). */
export function CoauthorStack(props: { coauthors: Coauthor[] }): ReactNode {
  if (props.coauthors.length === 0) return null;
  const handles = props.coauthors
    .map((coauthor) => coauthor.handle ?? coauthor.account_id)
    .join(', ');
  return (
    <span role="img" aria-label={media('coauthors.label', { handles })}>
      <AvatarGroup>
        {props.coauthors.map((coauthor) => (
          <AccountAvatar
            key={coauthor.account_id}
            size="sm"
            avatarUrl={coauthor.avatar_url}
            name={coauthor.handle}
          />
        ))}
      </AvatarGroup>
    </span>
  );
}
